export function normalizeToken(token: string): string {
  return token.replace(/ /g, '+');
}

export function mapAttendeeToCallPlan(attendee: any): Record<string, any> {
  const address = attendee?.address || {};
  const license = (attendee?.licenses && attendee.licenses[0]) || {};

  return {
    id: attendee?.id,
    first_name: attendee?.first_name || '',
    middle_name: attendee?.middle_name || '',
    last_name: attendee?.last_name || '',
    affiliation: attendee?.affiliation || address.affiliation || '',
    address1: address.address1 || '',
    address2: address.address2 && address.address2 !== 'NaN' ? address.address2 : '',
    city: address.city || '',
    state: address.state || '',
    zip: address.zip || '',
    hcp_type: attendee?.hcp_type || '',
    specialty: attendee?.specialties || [],
    vendor_code: attendee?.vendor_code || '',
    npi_id: attendee?.npi_no || '',
    state_license_number: license.license || '',
    license_state: license.state || '',
    cell_phone: attendee?.cell_phone || address.phone || '',
    email: attendee?.email || address.email || '',
    degree: (attendee?.degrees || []).filter(Boolean).join(', '),
    meal_opt_out: attendee?.meal_opt_out || false,
    signature: '',
  };
}

/**
 * Default-view filter: who should appear in the attendee list before a search.
 * Per client request, keep this simple: list everyone EXCEPT attendees whose
 * RSVP is `declined` or `canceled`.
 *
 * The source of truth is `attendee_status.id` — the same composite status the
 * admin Audience page displays. It reflects the attendee's *current* state
 * (e.g. 'signed' after sign-in), which can differ from the historical
 * `registered_status_id` (an attendee may show 'signed' here while their old
 * `registered_status_id` still reads 'canceled'). Falling back to the raw
 * status fields when the composite is missing.
 */
export function isAttendeeEligibleForSignIn(attendee: any): boolean {
  const excluded = new Set(['declined', 'canceled']);
  const effective = attendee?.attendee_status?.id;
  if (effective) {
    return !excluded.has(effective);
  }
  if (excluded.has(attendee?.registered_status_id)) return false;
  if (excluded.has(attendee?.invited_status_id)) return false;
  return true;
}

export function mapCallPlanContactToCallPlan(contact: any): Record<string, any> {
  const specialties = Array.isArray(contact?.specialties)
    ? contact.specialties.filter(Boolean)
    : (contact?.specialty ? [contact.specialty] : []);
  const degrees = Array.isArray(contact?.degrees)
    ? contact.degrees.filter(Boolean).join(', ')
    : (contact?.degree || '');

  return {
    // No `id` — this person isn't an event attendee yet. POST (create) path is used at submit.
    first_name: contact?.first_name || '',
    middle_name: contact?.middle_name || '',
    last_name: contact?.last_name || '',
    affiliation: contact?.affiliation || '',
    address1: contact?.address1 || '',
    address2: contact?.address2 && contact.address2 !== 'NaN' ? contact.address2 : '',
    city: contact?.city || '',
    state: contact?.state || '',
    zip: contact?.zip || '',
    hcp_type: contact?.hcp_type || '',
    specialty: specialties[0] || '',
    vendor_code: contact?.vendor_code || '',
    npi_id: contact?.npi_no || contact?.npi_id || '',
    state_license_number: contact?.license_number || contact?.state_license_number || '',
    license_state: contact?.license_state || '',
    cell_phone: contact?.cell_phone || contact?.phone || '',
    email: contact?.email || '',
    degree: degrees,
    meal_opt_out: false,
    walked_in: true,
    attended_status_id: 'attended',
    contact_id: contact?.type === 'contact' ? contact?.id : (contact?.contact_id || undefined),
    call_plan_id: contact?.type === 'call_plan' ? contact?.id : undefined,
    signature: '',
  };
}

export function getErrorMessage(err: any, fallback = 'Something went wrong'): string {
  return err?.error?.error?.message
    || err?.error?.message
    || err?.message
    || fallback;
}

export function parseQuestionChoices(choices: unknown): string[] {
  if (!choices) return [];
  if (Array.isArray(choices)) return choices.map((c) => String(c));
  if (typeof choices !== 'string') return [];
  const trimmed = choices.trim();
  if (!trimmed) return [];
  if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed.map((c) => String(c));
    } catch {
      // fall through to delimiter-based parsing
    }
  }
  const separator = trimmed.includes('\n') ? '\n' : ',';
  return trimmed.split(separator).map((c) => c.trim()).filter((c) => c !== '');
}

